import apiClient from '@/shared/services/api/apiClient';

import type {
  CandidateApplicationData,
  ResumeParsedData,
  ResumePrefillStatus,
} from '@/modules/candidate/types/candidateApplication.types';


export interface CreateDraftPayload {
  jobId: string;
  source?: string;
  referralToken?: string;
  referralUtm?: {
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_content?: string;
    utm_term?: string;
  };
}


export interface UploadResumePayload {
  draftId: string;
  file: File;
}


export interface UploadVideoPayload {
  draftId: string;
  file: File;
}


export interface UpdateCandidateProfilePayload
  extends CandidateApplicationData {
  draftId: string;
}


export interface ResumePrefillStatusResponse {
  draftId: string;

  resumePrefillStatus:
  | 'IDLE'
  | 'QUEUED'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'FAILED';

  resumePrefillProgress: number;

  resumePrefillError: string | null;

  resumeParsedData:
  | ResumeParsedData
  | null;

  resumeParsedText: string | null;

  resumeParserVersion: string | null;

  resumeExtractionVersion: number;

  resumePrefillStartedAt: string | null;

  resumePrefillCompletedAt: string | null;
}


export const createDraftApplication = async (
  payload: CreateDraftPayload
) => {
  const response = await apiClient.post(
    '/drafts',
    payload
  );

  return response.data;
};


export const uploadResume = async ({
  draftId,
  file,
}: UploadResumePayload) => {
  const formData = new FormData();

  formData.append(
    'resume',
    file
  );

  const response =
    await apiClient.post(
      `/drafts/${draftId}/resume`,
      formData,
      {
        headers: {
          'Content-Type':
            'multipart/form-data',
        },
      }
    );

  return response.data;
};


/*
 * Resume prefill polling.
 *
 * showLoader: false is important here because this
 * endpoint is called repeatedly while the background
 * worker processes the resume.
 *
 * Without this flag, every polling request would
 * start/stop the global loader and cause the screen
 * to blink.
 */
export const getResumePrefillStatus = async (
  draftId: string
) => {
  const response =
    await apiClient.get(
      `/drafts/${draftId}/resume/status`,
      {
        showLoader: false,
      }
    );

  return response.data;
};


export const updateCandidateProfileByApplicationId =
  async (
    payload: UpdateCandidateProfilePayload
  ) => {
    const {
      draftId,
      ...candidateProfile
    } = payload;

    const response = await apiClient.patch(
      `/drafts/${draftId}`,
      candidateProfile,
      { showLoader: true } as any
    );

    return response.data;
  };

export const uploadVideo = async ({
  draftId,
  file,
}: UploadVideoPayload) => {
  const formData = new FormData();

  formData.append(
    'video',
    file
  );

  const response =
    await apiClient.post(
      `/drafts/${draftId}/video`,
      formData,
      {
        headers: {
          'Content-Type':
            'multipart/form-data',
        },
      }
    );

  return response.data;
};


export const submitApplication = async (
  draftId: string
) => {
  const response =
    await apiClient.post(
      `/drafts/${draftId}/submit`
    );

  return response.data;
};


export const getMyApplications =
  async () => {
    const response =
      await apiClient.get(
        '/applications/my-applications'
      );

    return response.data;
  };


export const getApplicationDetails = async (
  applicationId: string
) => {
  const response = await apiClient.get(
    `/applications/${applicationId}/details`,
    { showLoader: true } as any
  );

  return response.data;
};